/**
 * Piccard UI Library - PrimaryButton Component
 *
 * Flat, pill-shaped button with editorial styling
 */

import type { ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

export interface PrimaryButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Size of the button
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg'

  /**
   * Whether the button should take full width
   * @default false
   */
  fullWidth?: boolean

  /**
   * Button content
   */
  children: React.ReactNode
}

/**
 * Primary button with flat editorial styling
 *
 * Features:
 * - Flat primary color background
 * - Pill-shaped (rounded-full)
 * - Hover/active scale interaction
 * - Multiple sizes (sm, md, lg)
 * - Full width option
 */
export function PrimaryButton({
  size = 'md',
  fullWidth = false,
  className,
  children,
  ...props
}: PrimaryButtonProps) {
  const sizeClasses = {
    sm: 'px-6 py-2.5 text-sm',
    md: 'px-8 py-3 text-base',
    lg: 'px-10 py-4 text-lg',
  }

  return (
    <button
      className={cn(
        // Base styles
        'font-bold text-white rounded-full',
        // Flat background (both variants use flat primary)
        'bg-primary',
        // Size
        sizeClasses[size],
        // Interaction states
        'transition-all duration-200',
        'hover:bg-primary-active hover:shadow-card',
        'active:scale-95',
        // Subtle shadow
        'shadow-subtle',
        // Focus ring for accessibility
        'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2',
        // Custom classes
        className,
        // Full width
        fullWidth && 'w-full'
      )}
      {...props}
    >
      {children}
    </button>
  )
}
